<template>
  <b-form @submit.stop.prevent="onSubmit" class="form-component">
    <GeneralError :show="showGeneralError" :text="generalError" />
    <b-form-group
      v-for="(field, key) in fields"
      :key="key"
      :id="`${key}-group`"
      label-class="font-weight-bold"
      :class="`form-group-${key}`"
      :description="
        !$slots['description-' + key] ? field.description : undefined
      "
    >
      <!-- Slot forwarding to define a custom label -->
      <template v-if="showLabel(field.type) && $slots['label-' + key]" #label>
        <slot :name="'label-' + key" />
      </template>
      <!-- Custom label for textarea with a max length defined -->
      <template v-else-if="field.type === 'textarea' && field.maxLength" #label>
        {{ field.label }}
        <span
          v-if="
            showOptional && (!field.validations || !field.validations.required)
          "
          class="font-weight-normal text-dark-50"
        >
          (optional)
        </span>
        <span
          :class="`float-right ${
            form[key] && form[key].length > field.maxLength ? 'text-danger' : ''
          }`"
        >
          {{ form[key] ? form[key].length : 0 }}/{{ field.maxLength }}
        </span>
      </template>
      <!-- Default label if not defined otherwise -->
      <template v-else-if="showLabel(field.type)" #label>
        {{ field.label }}
        <span
          v-if="
            showOptional && (!field.validations || !field.validations.required)
          "
          class="font-weight-normal text-dark-50"
        >
          (optional)
        </span>
      </template>

      <!-- Input field -->
      <b-form-input
        v-if="inputFieldTypes.includes(field.type)"
        v-model="form[key]"
        :id="key"
        :name="key"
        :type="field.type"
        :state="!field.disabled ? validateState(key) : null"
        :disabled="disabled || field.disabled"
        :placeholder="field.placeholder"
        :autocomplete="field.autocomplete"
        :class="inputClass"
        @input="onInput"
      />
      <b-form-input
        v-else-if="field.type === 'hidden'"
        v-model="form[key]"
        v-show="false"
        :id="key"
        :name="key"
        :type="field.type"
        :state="!field.disabled ? validateState(key) : null"
        :disabled="disabled || field.disabled"
        :placeholder="field.placeholder"
        :autocomplete="field.autocomplete"
        :class="inputClass"
        @input="onInput"
        hidden
      />
      <!-- Checkbox -->
      <b-form-checkbox
        v-else-if="field.type === 'checkbox'"
        v-model="form[key]"
        :id="key"
        :name="key"
        :class="validateState(key) ? '' : 'is-invalid'"
        :disabled="disabled || field.disabled"
        @input="onInput"
      >
        <slot :name="'label-' + key">
          {{ field.label }}
        </slot>
      </b-form-checkbox>
      <!-- Percentage -->
      <b-input-group
        v-else-if="field.type === 'percentage'"
        append="%"
        :class="`input-group-solid input-group-percentage ${
          disabled || field.disabled ? 'disabled' : ''
        }`"
      >
        <b-form-input
          v-model="form[key]"
          :id="key"
          :name="key"
          type="number"
          :state="!(disabled || field.disabled) ? validateState(key) : null"
          :disabled="disabled || field.disabled"
          :placeholder="field.placeholder"
          :autocomplete="field.autocomplete"
          :class="inputClass"
          @input="onInput"
        />
      </b-input-group>
      <!-- Textarea -->
      <b-form-textarea
        v-else-if="field.type === 'textarea'"
        v-model="form[key]"
        :id="key"
        :name="key"
        :type="field.type"
        :state="validateState(key)"
        :disabled="disabled || field.disabled"
        :placeholder="field.placeholder"
        :class="inputClass"
        :rows="3"
        @input="onInput"
      />
      <!-- Select -->
      <b-select
        v-else-if="field.type === 'select'"
        v-model="form[key]"
        :options="field.options"
        :id="key"
        :name="key"
        :state="validateState(key)"
        :disabled="disabled || field.disabled"
        :class="inputClass"
        @input="onInput"
      >
        <template #first>
          <b-form-select-option :value="null">
            Select an option
          </b-form-select-option>
        </template>
      </b-select>
      <!-- Tags -->
      <TagsInput
        v-else-if="field.type === 'tags'"
        v-model="form[key]"
        :id="key"
        :name="key"
        :state="validateState(key)"
        :disabled="disabled || field.disabled"
        :placeholder="field.placeholder"
        :inputClass="inputClass"
        :getSuggestions="field.getSuggestions"
        @input="onInput"
      />
      <multiselect
        v-else-if="field.type === 'multiselect'"
        :id="key"
        v-model="form[key]"
        :options="field.options"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="true"
        :preserve-search="true"
        :preselect-first="false"
        :placeholder="field.placeholder"
        @input="onInput"
      >
      </multiselect>
      <!-- Image -->
      <div
        v-else-if="field.type === 'image'"
        :class="`image-field-container ${form[key] ? 'has-image' : ''}`"
      >
        <button class="remove-image-button" @click.prevent="form[key] = null">
          <inline-svg :src="require('iconoir/icons/cancel.svg')" />
        </button>
        <img
          v-if="
            form[key] && (typeof form[key] === 'string' || isImage(form[key]))
          "
          :src="getImageSrc(form[key])"
          class="image-upload-image"
        />
        <b-form-file
          v-else
          v-model="form[key]"
          :id="key"
          :name="key"
          :state="validateState(key)"
          :disabled="disabled || field.disabled"
          :placeholder="field.placeholder"
          :drop-placeholder="field.dropPlaceholder"
          :class="`${inputClass} image-upload-field`"
          @input="onInput"
        />
      </div>
      <!-- Errors -->
      <template v-for="(message, error) in field.errors">
        <b-form-invalid-feedback
          v-if="hasError(key, error)"
          :key="error"
          force-show
        >
          {{ message }}
        </b-form-invalid-feedback>
      </template>
      <b-form-invalid-feedback v-if="errors && errors[key]" force-show>
        {{ errors[key].join(", ") }}
      </b-form-invalid-feedback>
      <!-- Link -->
      <router-link
        v-if="field.link"
        :to="field.link.to"
        class="d-inline-block mt-2"
      >
        {{ field.link.text }}
      </router-link>
      <slot v-if="$slots['description-' + key]" :name="'description-' + key" />
    </b-form-group>

    <slot
      name="submitRow"
      :isLoading="isLoading"
      :submitLabel="submitLabel || $t('GENERAL.FORM.SUBMIT')"
    >
      <b-row>
        <b-col class="d-flex justify-content-end">
          <Button
            :isLoading="isLoading"
            :disabled="disabled"
            :variant="submitMuted && !hasInput ? 'secondary' : 'primary'"
          >
            {{ submitLabel || $t("GENERAL.FORM.SUBMIT") }}
          </Button>
        </b-col>
      </b-row>
    </slot>
  </b-form>
</template>

<script>
import { vuelidate } from "@/mixins/vuelidate";
import { isImage } from "@/lib/image";
import GeneralError from "../GeneralError.vue";
import cloneDeep from "lodash/cloneDeep";
import Multiselect from "vue-multiselect";
import Button from "../buttons/Button.vue";
import TagsInput from "./TagsInput.vue";

/**
 * Dynamically renders a form based on structured data about
 * the fields it contains, including integration with
 * validation of vuelidate
 *
 * Custom label (html-)content using template slots:
 * <Form :fields="{ name: { type: 'text' } }">
 *   <template #label-name>
 *     Enter your <strong>name</strong>:
 *   </template>
 *  </Form>
 */

export default {
  mixins: [vuelidate],
  components: {
    GeneralError,
    Button,
    TagsInput,
    Multiselect
  },
  props: {
    /**
     * Field description
     * {
     *   email: {
     *     type: "text" | "email" | "number" | "password" | "checkbox" | "percentage" | "textarea" | "select" | "image" | "tags",
     *     label: "E-mail address",
     *     placeholder: "Enter your e-mail addresss...",
     *     autocomplete: "email",
     *     disabled: false,
     *     validation: { required }, // vuelidate validation object
     *     options: [{ value: 'ft', text: 'Fair Trade' }, ...], // in case of type = "select"
     *     getSuggestions: (input) => ['abc', 'def'].filter(v => v.includes(input)), // in case of type = "tags", function which returns string array
     *     maxLength: 128, // Max length (for textarea)
     *     errors: {
     *       required: "E-mail field is required"
     *     },
     *     link: {
     *       text: "A link to some page",
     *       to: "url or routing object which will be passed to <router-link>",
     *     }
     *   },
     *   ...
     * }
     */
    fields: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Input form data
     */
    input: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * External errors (from the server probably)
     */
    errors: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * To show (optional) behind label of non-required fields, or not
     */
    showOptional: {
      type: Boolean,
      default: false
    },
    /**
     * Shows a general error message for unknown errors
     */
    showGeneralError: {
      type: Boolean
    },
    /**
     * Shows a general error message for unknown errors
     */
    generalError: {
      type: String,
      default() {
        return null;
      }
    },
    /**
     * Shows spinner animation on submit button
     */
    isLoading: {
      type: Boolean
    },
    /**
     * Size of form fields, and spacing around them
     */
    size: {
      type: String,
      default() {
        return "md";
      },
      validator(value) {
        return ["sm", "md", "lg"].includes(value);
      }
    },
    disabled: {
      type: Boolean
    },
    /**
     * Text on submit button
     */
    submitLabel: {
      type: String,
      default() {
        return null;
      }
    },
    /**
     * Show submit button as secondary variant
     * as long as no form field edit has been made
     */
    submitMuted: {
      type: Boolean
    }
  },
  data() {
    return {
      form: cloneDeep(this.input),
      hasInput: false,
      inputFieldTypes: ["text", "email", "number", "password"]
    };
  },
  validations() {
    // Dynamically generate vuelidate validation schema
    return {
      form: this.fieldKeys.reduce((validations, name) => {
        validations[name] = this.fields[name].validations || {};
        return validations;
      }, {})
    };
  },
  computed: {
    fieldKeys() {
      return Object.keys(this.fields);
    },
    fieldValues() {
      return Object.values(this.fields);
    },
    inputClass() {
      return `form-control form-control-solid ${
        this.size === "lg" ? "h-auto py-5 px-6" : ""
      }`;
    }
  },
  methods: {
    isImage: isImage,
    getImageSrc(field) {
      return typeof field === "string" ? field : URL.createObjectURL(field);
    },
    showLabel(type) {
      return type !== "checkbox" && type !== "hidden";
    },
    onInput() {
      this.hasInput = true;
      this.$emit("input", this.form);
    },
    onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      this.$emit("submit", this.form);
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import "~@/assets/sass/variables/main";

.input-group-percentage {
  max-width: 200px;
}
.image-field-container {
  position: relative;
  &.has-image {
    background-color: $light;
    border-radius: 0.42rem;
    .remove-image-button {
      display: flex;
    }
  }
  .remove-image-button {
    position: absolute;
    display: none;
    right: -12.5px;
    top: -12.5px;
    background-color: $primary;
    border: 0;
    color: white;
    border-radius: 20px;
    width: 25px;
    height: 25px;
    justify-content: center;
    align-items: center;
  }
  .image-upload-image {
    width: calc(100% - 4rem);
    margin: 1rem 2rem;
  }
}
</style>
<style lang="scss">
@import "~@/assets/sass/variables/main";
@import "~bootstrap/scss/mixins";

.form-component {
  legend.col-form-label {
    padding-bottom: 0.5rem;
  }
  .image-upload-field.form-control-solid {
    border: 0;
    background-color: transparent;
    .custom-file-label {
      border: 0;
      background-color: #f3f6f9;
      &:after {
        border: 1px solid #ccc;
        height: 100%;
        background-color: #fff;
      }
    }
  }
}
</style>
